import ChatNotificationControl from "./ChatNotificationControl";
import { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useChat } from "@/contexts/ChatContext";
import { useGetChatMentionsQuery } from "@/services/chat";
export default function ConversationList({
  selectedId,
  onCreate,
}: {
  selectedId?: string;
  onCreate: () => void;
}) {
  const { actorId, summary, conversationLists, loadConversations } = useChat();
  const [scope, setScope] = useState<"mine" | "others">("mine"),
    [q, setQ] = useState(""),
    [mentions, setMentions] = useState(false),
    [mentionPage, setMentionPage] = useState(0),
    [loading, setLoading] = useState(false),
    [failed, setFailed] = useState(false);
  const safeScope = summary?.isAdmin ? scope : "mine";
  useEffect(() => {
    let live = true;
    setLoading(true);
    setFailed(false);
    loadConversations({ scope: safeScope, q }).then((page) => {
      if (live) {
        setLoading(false);
        setFailed(!page);
      }
    });
    return () => {
      live = false;
    };
  }, [safeScope, q, loadConversations]);
  const mentionsQuery = useGetChatMentionsQuery(
    { actorId: actorId!, page: mentionPage },
    { skip: !actorId || !mentions },
  );
  const page = conversationLists[safeScope];
  return (
    <aside className="chat-sidebar" aria-label="Conversations">
      <div className="chat-sidebar-heading">
        <h1>Messagerie</h1>
        <Button
          variant="light"
          onClick={onCreate}
          aria-label="Nouvelle conversation"
        >
          ＋
        </Button>
      </div>
      <ChatNotificationControl />
      <div className="chat-sidebar-controls">
        <Form.Control
          aria-label="Rechercher une conversation"
          placeholder="Chercher une conversation"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="chat-list-tabs">
          <Button
            variant={!mentions && safeScope === "mine" ? "primary" : "light"}
            onClick={() => {
              setScope("mine");
              setMentions(false);
            }}
          >
            Mes conversations
          </Button>
          {summary?.isAdmin && (
            <Button
              variant={
                !mentions && safeScope === "others" ? "primary" : "light"
              }
              onClick={() => {
                setScope("others");
                setMentions(false);
              }}
            >
              Autres conversations
            </Button>
          )}
          <Button
            variant={mentions ? "primary" : "light"}
            onClick={() => setMentions(true)}
          >
            @ mentions
            {summary?.mentionCount ? ` (${summary.mentionCount})` : ""}
          </Button>
        </div>
      </div>
      <div className="chat-conversation-scroll" aria-busy={loading}>
        {mentions ? (
          <>
            <h2>Mes mentions non lues</h2>
            {mentionsQuery.currentData?.data.map((m) => (
              <Link
                className="chat-conversation"
                key={m.messageId}
                to={`/chat/${m.conversationId}?message=${m.messageId}`}
              >
                <span>
                  Voir la mention du{" "}
                  {new Date(m.createdAt).toLocaleDateString("fr-FR")}
                </span>
              </Link>
            ))}
            {mentionsQuery.isError && (
              <Alert variant="danger">
                Impossible de charger les mentions.
              </Alert>
            )}
            {mentionsQuery.currentData?.data.length === 0 && (
              <p>Aucune mention non lue.</p>
            )}
            {mentionPage > 0 && (
              <Button
                variant="link"
                onClick={() => setMentionPage(0)}
              >
                Premières mentions
              </Button>
            )}
            {mentionsQuery.currentData?.nextCursor && (
              <Button
                variant="link"
                onClick={() =>
                  setMentionPage((page) => page + 1)
                }
              >
                Mentions suivantes
              </Button>
            )}
          </>
        ) : (
          <>
            {(["group", "direct"] as const).map((kind) => (
              <section key={kind}>
                <h2>{kind === "group" ? "Groupes" : "Messages privés"}</h2>
                {page?.data
                  .filter((c) => c.kind === kind)
                  .map((c) => (
                    <Link
                      key={c.id}
                      to={`/chat/${c.id}`}
                      className={`chat-conversation ${selectedId === c.id ? "is-active" : ""}`}
                      aria-current={selectedId === c.id ? "page" : undefined}
                    >
                      <span className="chat-avatar" aria-hidden="true">
                        {kind === "group"
                          ? "#"
                          : c.name.slice(0, 2).toUpperCase()}
                      </span>
                      <span className="chat-conversation-text">
                        <strong>{c.name}</strong>
                        {safeScope === "others" && summary?.isAdmin && kind === "group" && !c.hasActiveAdmin && (
                          <span>Sans administrateur actif</span>
                        )}
                        <span>
                          {c.lastMessage?.parts
                            .map((p) =>
                              p.type === "text" ? p.text : `@${p.label}`,
                            )
                            .join("") || "Aucun message"}
                        </span>
                      </span>
                      {c.unreadCount > 0 && (
                        <span
                          className="chat-badge"
                          aria-label={`${c.unreadCount} non lus`}
                        >
                          {c.unreadCount}
                        </span>
                      )}
                    </Link>
                  ))}
              </section>
            ))}
            {!loading && page?.data.length === 0 && (
              <p className="chat-empty">Aucune conversation.</p>
            )}
            {failed && (
              <Alert variant="warning">
                Impossible de charger les conversations.{" "}
                <Button
                  variant="link"
                  onClick={() =>
                    loadConversations({ scope: safeScope, q }).then((p) =>
                      setFailed(!p),
                    )
                  }
                >
                  Réessayer
                </Button>
              </Alert>
            )}
            {page?.nextCursor && (
              <Button
                variant="link"
                disabled={loading}
                onClick={async () => {
                  setLoading(true);
                  const p = await loadConversations({
                    scope: safeScope,
                    q,
                    cursor: page.nextCursor!,
                  });
                  setLoading(false);
                  setFailed(!p);
                }}
              >
                Plus de conversations
              </Button>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
